import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import type { PdlExistingLoansDto } from '../../api/admin';
import { PDL_EVIDENCE_COPY } from '../../lib/pdlAdjudicatorUiCopy';
import { PDL_INNER_RADIUS } from '../../lib/pdlDecisionChrome';
import { buildPdlObligationTable } from '../../lib/pdlExistingLoansRows';

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Rule strengths, derived from the text colour so they read clearly on the tinted
 * tile in both light and dark mode (pure black would be harsh in dark).
 * Frame + header/total separators are strong; inner gridlines are lighter.
 */
const ruleStrong = (t: Theme) => alpha(t.palette.text.primary, 0.42);
const ruleSoft = (t: Theme) => alpha(t.palette.text.primary, 0.2);

/** Outer corner radius in px — matches the tile's inner radius. */
const cornerRadius = (t: Theme) => `${Number(t.shape.borderRadius) * PDL_INNER_RADIUS}px`;

/**
 * Cells draw the whole grid, frame included (border-collapse: separate), so the
 * header row can carry its own accent outline while body rows keep the grey frame.
 * Every cell: right + bottom gridline. First / last cell: grey outer side.
 */
const CELL_SX = {
  borderRight: '1px solid',
  borderBottom: '1px solid',
  borderColor: ruleSoft,
  px: 1.25,
  py: 0.75,
  fontSize: '0.875rem',
  '&:first-of-type': { borderLeft: '1.5px solid', borderLeftColor: ruleStrong },
  '&:last-of-type': { borderRight: '1.5px solid', borderRightColor: ruleStrong },
} as const;

export type PdlTableAccent = 'success' | 'warning' | 'info';

/**
 * Column-title row outlined in the tile's accent (teal by default, amber when the
 * tile carries risk flags) on all four sides; the rest of the table stays grey.
 */
function headCellSx(accent: PdlTableAccent) {
  const accentRule = (t: Theme) => t.palette[accent].main;
  return {
    ...CELL_SX,
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontSize: '0.8rem',
    py: 1.25,
    borderTop: '2px solid',
    borderTopColor: accentRule,
    borderBottom: '2px solid',
    borderBottomColor: accentRule,
    '&:first-of-type': { borderLeft: '2px solid', borderLeftColor: accentRule, borderTopLeftRadius: cornerRadius },
    '&:last-of-type': { borderRight: '2px solid', borderRightColor: accentRule, borderTopRightRadius: cornerRadius },
  } as const;
}

/**
 * Header cells: no fill, bold primary text, framed top and bottom by the accent rule.
 * Applied on TableHead with the same `& .MuiTableCell-head` selector the theme's
 * MuiTableHead override uses, so it out-ranks that grey default inside this table only.
 */
const HEAD_BAND_SX = {
  '& .MuiTableCell-head': {
    color: 'text.primary',
    background: 'transparent',
    fontWeight: 800,
    fontSize: '0.8rem',
  },
} as const;

const SERIAL_SX = { width: '4.5rem', textAlign: 'center', fontVariantNumeric: 'tabular-nums' } as const;

const NUM_SX = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

/** Payments collapses on phones to keep the amount readable. */
const PAYMENTS_COL_SX = { display: { xs: 'none', sm: 'table-cell' } } as const;

/**
 * Itemised ELOD obligations inside the "Existing debts (ELOD)" tile:
 * S.No. · Lender · Frequency · Payments · Monthly, closed by the monthly total.
 * Renders nothing for applications evaluated before obligations were stamped.
 */
export function PdlExistingLoansTable({
  pdlExistingLoans,
  accent = 'success',
}: {
  pdlExistingLoans: PdlExistingLoansDto | null | undefined;
  accent?: PdlTableAccent;
}) {
  const table = buildPdlObligationTable(pdlExistingLoans);
  if (!table) return null;
  const head = headCellSx(accent);

  return (
    <Box
      data-testid="pdl-existing-loans-table"
      sx={{
        borderRadius: PDL_INNER_RADIUS,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: (t: Theme) => `0 1px 3px ${alpha(t.palette.common.black, t.palette.mode === 'dark' ? 0.4 : 0.08)}`,
      }}
    >
      <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <TableHead sx={HEAD_BAND_SX}>
          <TableRow>
            <TableCell sx={[head, SERIAL_SX]}>S.No.</TableCell>
            <TableCell sx={head}>Lender</TableCell>
            <TableCell sx={[head, { width: '9rem' }]}>Frequency</TableCell>
            <TableCell sx={[head, NUM_SX, PAYMENTS_COL_SX, { width: '7rem' }]}>Payments</TableCell>
            <TableCell sx={[head, NUM_SX, { width: '8rem' }]}>Monthly</TableCell>
          </TableRow>
        </TableHead>
        {/* Last data row closes onto the total with the strong rule. */}
        <TableBody
          sx={{ '& tr:nth-last-of-type(2) td': { borderBottomWidth: '1.5px', borderBottomColor: ruleStrong } }}
        >
          {table.rows.map((row) => (
            <TableRow key={`${row.serial}-${row.lender}`}>
              <TableCell sx={{ ...CELL_SX, ...SERIAL_SX, color: 'text.secondary' }}>
                {row.serial}
              </TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 700, overflowWrap: 'anywhere' }}>{row.lender}</TableCell>
              <TableCell sx={{ ...CELL_SX, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {row.frequency}
              </TableCell>
              <TableCell sx={{ ...CELL_SX, ...NUM_SX, ...PAYMENTS_COL_SX, color: 'text.secondary' }}>
                {row.paymentCount > 0 ? row.paymentCount : '—'}
              </TableCell>
              <Tooltip
                title={
                  row.grossMonthlyAmount != null
                    ? `Gross ${fmt(row.grossMonthlyAmount)}/mo, net of money this lender sent back`
                    : ''
                }
              >
                <TableCell sx={{ ...CELL_SX, ...NUM_SX, fontWeight: 800 }}>{fmt(row.monthlyAmount)}/mo</TableCell>
              </Tooltip>
            </TableRow>
          ))}
          <TableRow
            sx={{
              bgcolor: (t: Theme) => alpha(t.palette.text.primary, 0.07),
              '& td': { borderBottomWidth: '1.5px', borderBottomColor: ruleStrong },
              '& td:first-of-type': { borderBottomLeftRadius: cornerRadius },
              '& td:last-of-type': { borderBottomRightRadius: cornerRadius },
            }}
          >
            <TableCell colSpan={2} sx={{ ...CELL_SX, fontWeight: 800 }}>
              {PDL_EVIDENCE_COPY.obligationsTotal}
            </TableCell>
            <TableCell sx={CELL_SX} />
            <TableCell sx={{ ...CELL_SX, ...PAYMENTS_COL_SX }} />
            <TableCell sx={{ ...CELL_SX, ...NUM_SX, fontWeight: 800 }}>{fmt(table.totalMonthly)}/mo</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}
